<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Radio Button</h1>
    <p class="doc__lead">
      Radio button individual con círculo visual personalizado, punto deslizante con transición CSS
      y mismos tokens que <code>ui-checkbox</code>. Los grupos se manejan en el componente padre
      compartiendo el atributo <code>name</code> y comparando el <code>value</code> con un signal.
    </p>
  </header>

  <!-- ── Estados ────────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section title="Normal" description="Sin seleccionar.">
      <ui-radio label="Opción A" name="estado-normal" value="a" />
    </app-example-section>

    <app-example-section title="Seleccionado" description="checked=true.">
      <ui-radio label="Opción B" name="estado-checked" value="b" [checked]="true" />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde y punto en rojo."
    >
      <div class="doc-example-stack">
        <ui-radio
          label="Método de pago"
          name="estado-error"
          value="x"
          [required]="true"
          [invalid]="isInvalid()"
          message="Debes seleccionar una opción."
        />
        <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
          {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
        </button>
      </div>
    </app-example-section>

    <app-example-section title="Deshabilitado sin selección" description="Opacidad 0.5.">
      <ui-radio label="No disponible" name="estado-disabled" value="d" [disabled]="true" />
    </app-example-section>

    <app-example-section title="Deshabilitado seleccionado" description="Valor fijo, sin interacción.">
      <ui-radio label="Seleccionado fijo" name="estado-disabled-checked" value="e" [checked]="true" [disabled]="true" />
    </app-example-section>
  </div>

  <!-- ── Grupos ─────────────────────────────────── -->
  <h2 class="doc-section-title">Grupos</h2>

  <div class="doc-grid">
    <app-example-section
      title="Grupo horizontal"
      description="Opciones en fila. Signal compartido para la selección."
    >
      <div class="doc-radio-group doc-radio-group--row">
        @for (f of fruits; track f.value) {
          <ui-radio
            [name]="'fruta'"
            [value]="f.value"
            [label]="f.label"
            [checked]="fruit() === f.value"
            (checkedChange)="$event && fruit.set(f.value)"
          />
        }
      </div>
      <p class="doc-value-preview">Seleccionado: <strong>{{ fruit() }}</strong></p>
    </app-example-section>

    <app-example-section
      title="Grupo vertical"
      description="Opciones apiladas, con descripción por opción."
    >
      <div class="doc-radio-group doc-radio-group--col">
        @for (p of plans; track p.value) {
          <ui-radio
            [name]="'plan'"
            [value]="p.value"
            [label]="p.label"
            [description]="p.description"
            [checked]="plan() === p.value"
            (checkedChange)="$event && plan.set(p.value)"
          />
        }
      </div>
      <p class="doc-value-preview">Plan seleccionado: <strong>{{ plan() }}</strong></p>
    </app-example-section>

    <app-example-section
      title="Grupo con opciones mixtas"
      description="Algunas opciones deshabilitadas dentro del grupo."
    >
      <div class="doc-radio-group doc-radio-group--col">
        @for (pm of paymentMethods; track pm.value) {
          <ui-radio
            [name]="'pago'"
            [value]="pm.value"
            [label]="pm.label"
            [disabled]="pm.disabled ?? false"
            [checked]="payment() === pm.value"
            (checkedChange)="$event && payment.set(pm.value)"
          />
        }
      </div>
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Grupo básico con signal:</p>
    <pre class="doc-block__pre" role="code"><code>selected = signal('opcion-a');

&lt;ui-radio
  name="grupo"
  value="opcion-a"
  label="Opción A"
  [checked]="selected() === 'opcion-a'"
  (checkedChange)="$event &amp;&amp; selected.set('opcion-a')"
/&gt;
&lt;ui-radio
  name="grupo"
  value="opcion-b"
  label="Opción B"
  [checked]="selected() === 'opcion-b'"
  (checkedChange)="$event &amp;&amp; selected.set('opcion-b')"
/&gt;</code></pre>

    <p class="doc-block__p">Grupo dinámico desde array de opciones:</p>
    <pre class="doc-block__pre" role="code"><code>options = [
  &#123; value: 'a', label: 'Opción A' &#125;,
  &#123; value: 'b', label: 'Opción B' &#125;,
];
selected = signal('a');

&#64;for (opt of options; track opt.value) &#123;
  &lt;ui-radio
    name="mi-grupo"
    [value]="opt.value"
    [label]="opt.label"
    [checked]="selected() === opt.value"
    (checkedChange)="$event &amp;&amp; selected.set(opt.value)"
  /&gt;
&#125;</code></pre>

    <p class="doc-block__p">Con Reactive Forms:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-radio
  name="metodo"
  value="card"
  label="Tarjeta"
  [checked]="form.controls.metodo.value === 'card'"
  (checkedChange)="$event &amp;&amp; form.controls.metodo.setValue('card')"
  [invalid]="form.controls.metodo.invalid &amp;&amp; form.controls.metodo.touched"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ─────────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checked</code></td><td><code>boolean</code> (model)</td><td><code>false</code></td><td>Estado del radio. Soporta <code>[(checked)]</code>.</td></tr>
        <tr><td><code>name</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Agrupa radios. Todos los radios del grupo deben compartir el mismo <code>name</code>.</td></tr>
        <tr><td><code>value</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Valor que identifica esta opción dentro del grupo.</td></tr>
        <tr><td><code>label</code></td><td><code>string</code></td><td><code>''</code></td><td>Texto principal junto al círculo.</td></tr>
        <tr><td><code>description</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto secundario bajo el label.</td></tr>
        <tr><td><code>required</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Obligatorio" y <code>aria-required</code>.</td></tr>
        <tr><td><code>showOptionalHint</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Opcional".</td></tr>
        <tr><td><code>disabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Deshabilita el radio.</td></tr>
        <tr><td><code>invalid</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Círculo en rojo y <code>aria-invalid</code>.</td></tr>
        <tr><td><code>message</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto del mensaje de error.</td></tr>
        <tr><td><code>showMessage</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Controla si el mensaje se renderiza.</td></tr>
        <tr><td><code>fieldId</code></td><td><code>string | undefined</code></td><td>Auto-generado</td><td>ID del input nativo.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ───────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checkedChange</code></td><td><code>boolean</code></td><td>Emite <code>true</code> al seleccionar el radio. Usar con <code>$event &amp;&amp; selected.set(value)</code>.</td></tr>
        <tr><td><code>blurred</code></td><td><code>FocusEvent</code></td><td>Emite cuando el radio pierde el foco.</td></tr>
        <tr><td><code>focused</code></td><td><code>FocusEvent</code></td><td>Emite cuando el radio recibe el foco.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ──────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Siempre define el mismo <code>name</code> en todos los radios del grupo para que el navegador gestione la selección exclusiva nativamente.</li>
      <li>Usa un signal único por grupo (<code>selected = signal('')</code>) y compara con <code>value</code> para determinar <code>[checked]</code>.</li>
      <li>El patrón <code>(checkedChange)="$event &amp;&amp; selected.set(value)"</code> garantiza que solo se actualiza el signal cuando el radio se activa (no cuando se desactiva).</li>
      <li>Para validación de grupo, aplica <code>[invalid]</code> a todos los radios del grupo o solo al último, según el diseño del formulario.</li>
      <li>Usa <code>description</code> en grupos de planes o tarifas donde cada opción necesita contexto adicional.</li>
    </ul>
  </section>
</article>
